import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import { useChat } from '../../../hooks/useChat';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

export default function MonthlyNarrative({ spendingData, totalSpent, totalBudget, dateLabel }) {
  const { user } = useAuth();
  const [narrative, setNarrative] = useState('');
  const [userName, setUserName] = useState('');
  const [hasGenerated, setHasGenerated] = useState(false);
  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-5', false);

  useEffect(() => {
    if (!user?.id) return;
    supabase?.from('user_profiles')?.select('full_name, income_range')?.eq('id', user?.id)?.single()?.then(({ data }) => {
        if (data?.full_name) {
          const firstName = data?.full_name?.split(' ')?.[0];
          setUserName(firstName);
        }
      });
  }, [user?.id]);

  const generateNarrative = useCallback(() => {
    if (!spendingData?.length || isLoading) return;
    setHasGenerated(true);

    const categoryBreakdown = spendingData?.map(d => {
      const change = d?.lastMonth > 0 ? (((d?.thisMonth - d?.lastMonth) / d?.lastMonth) * 100)?.toFixed(1) : null;
      return `${d?.category}: $${d?.thisMonth}${change !== null ? ` (${change > 0 ? '+' : ''}${change}% vs last month)` : ''}`;
    })?.join(', ');

    const budgetStatus = totalBudget > 0
      ? totalSpent > totalBudget
        ? `over budget by $${(totalSpent - totalBudget)?.toFixed(0)}`
        : `$${(totalBudget - totalSpent)?.toFixed(0)} under budget`
      : '';

    sendMessage([
      {
        role: 'system',
        content: `You are FinVisAR, a friendly personal finance advisor. Write a single conversational paragraph (2-4 sentences) summarizing the user's financial month. Be warm, specific with numbers, mention both positives and areas to improve. Start with "Hey ${userName || 'there'},". Keep it under 80 words. Do not use bullet points or headers.`,
      },
      {
        role: 'user',
        content: `Write a monthly financial summary for ${dateLabel}. Spending: ${categoryBreakdown}. Budget status: ${budgetStatus || 'no budget set'}. Total spent: $${totalSpent}.`,
      },
    ], { max_completion_tokens: 150 });
  }, [spendingData, isLoading, dateLabel, totalSpent, totalBudget, userName, sendMessage]);

  useEffect(() => {
    if (response && hasGenerated && !isLoading) {
      setNarrative(response);
    }
  }, [response, hasGenerated, isLoading]);

  // Auto-generate when data is available
  useEffect(() => {
    if (spendingData?.length > 0 && !hasGenerated && !isLoading) {
      generateNarrative();
    }
  }, [spendingData, hasGenerated]);

  if (!hasGenerated && !isLoading) return null;

  return (
    <div
      className="rounded-xl p-5 mb-6"
      style={{ background: 'linear-gradient(135deg, rgba(0,229,255,0.06) 0%, rgba(123,47,190,0.06) 100%)', border: '1px solid rgba(0,229,255,0.15)' }}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, rgba(0,229,255,0.2), rgba(123,47,190,0.2))' }}
        >
          <Icon name="MessageSquare" size={16} color="#00E5FF" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-[#00E5FF]">FinVisAR Monthly Summary</span>
              <span className="text-xs text-[#475569]">· {dateLabel}</span>
            </div>
            <button
              onClick={generateNarrative}
              disabled={isLoading}
              className="flex items-center gap-1 text-[10px] text-[#64748B] hover:text-[#94A3B8] transition-colors disabled:opacity-50"
            >
              <Icon name="RefreshCw" size={10} />
              Refresh
            </button>
          </div>
          {isLoading ? (
            <div className="space-y-2 animate-pulse">
              <div className="h-3 bg-[rgba(255,255,255,0.06)] rounded w-full" />
              <div className="h-3 bg-[rgba(255,255,255,0.04)] rounded w-5/6" />
              <div className="h-3 bg-[rgba(255,255,255,0.04)] rounded w-4/5" />
            </div>
          ) : error ? (
            <p className="text-[#64748B] text-sm italic">Unable to generate summary. Your spending data is loaded and ready to analyze.</p>
          ) : (
            <p className="text-[#CBD5E1] text-sm leading-relaxed italic">{narrative}</p>
          )}
        </div>
      </div>
    </div>
  );
}
